import markdownIt from "markdown-it";
import createDOMPurify, { type DOMPurify, type WindowLike } from "dompurify";

const ALLOWED_ATTR = ["href", "target", "rel", "style"];

let purify: DOMPurify;
function getPurify() {
  if (purify) return purify;
  if (typeof window !== "undefined") {
    purify = createDOMPurify(window);
  } else {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { JSDOM } = require("jsdom");
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    purify = createDOMPurify(new JSDOM("").window as WindowLike);
  }
  return purify;
}

const galleryTags = [
  "p",
  "br",
  "a",
  "strong",
  "em",
  "b",
  "i",
  "s",
  "del",
  "u",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "ul",
  "ol",
  "li",
  "hr",
  "mark",
  "span",
];

const basicTags = ["p", "br", "a", "strong", "em", "b", "i", "s", "del", "u", "mark", "span"];

type RenderOptions = {
  elements?: "basic" | "gallery";
};

const md = markdownIt({ html: true, breaks: true, linkify: true });
md.enable("strikethrough");

// Add target="_blank" and rel="noopener noreferrer" to external links
const defaultLinkOpen =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, _env, self) {
    return self.renderToken(tokens, idx, options);
  };

md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const href = tokens[idx].attrGet("href");
  if (href && /^https?:\/\//.test(href)) {
    tokens[idx].attrSet("target", "_blank");
    tokens[idx].attrSet("rel", "noopener noreferrer");
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};

function sanitizeStyles(purify: DOMPurify) {
  purify.addHook("afterSanitizeAttributes", node => {
    if (node.hasAttribute("style")) {
      const style = node.getAttribute("style") || "";
      const allowed = style.match(/(background-color|color)\s*:\s*[^;]+/gi);
      if (allowed) {
        node.setAttribute("style", allowed.join("; "));
      } else {
        node.removeAttribute("style");
      }
    }
  });
}

let hooksRegistered = false;

export default function renderMarkdown(markdown: string, options: RenderOptions = {}): string {
  const { elements } = options;
  const html = md.render(markdown);
  const purify = getPurify();

  if (!hooksRegistered) {
    sanitizeStyles(purify);
    hooksRegistered = true;
  }

  const allowedTags =
    elements === "gallery" ? galleryTags : elements === "basic" ? basicTags : undefined;

  const purifyConfig = allowedTags
    ? { ALLOWED_TAGS: allowedTags, ALLOWED_ATTR }
    : { ADD_ATTR: ["style"] };

  return purify.sanitize(html, purifyConfig);
}
